/** @format */

import CryptoService from "../../services/CryptoService";
import { ILoginFormProps } from "./LoginForm/LoginForm.types";
import { ITwoFALoginFormProps } from "./TwoFALoginForm/TwoFALoginForm.types";
import { ILogInResponseBody } from "../../apis/VoiceGainAuthSvcApi/VoiceGainAuthSvcApi.types";
import { ISession } from "../../services/SessionService/";
import { ITermsOfServiceModalProps } from "./TermsOfServiceModal/TermsOfServiceModal.types";
import { logIn } from "../../redux/authReducer/actions";
import LoginContentView from "./LoginContent.view";
import { v4 as uuidv4 } from "uuid";
import { message } from "antd";
import React from "react";
import SessionService from "../../services/SessionService";
import VoiceGainAuthSvcApi from "../../apis/VoiceGainAuthSvcApi";
import { IAppPropsStore } from "../../redux/appPropsReducer/types";

interface IProps {
  appProps: IAppPropsStore;
  logIn: typeof logIn;
}

interface IState {
  isProcessingLogIn: boolean;
  isProcessingTermsOfService: boolean;
  stagedSession?: ISession;
  termsOfServiceModalIsVisible: boolean;
  isTwoFALogin: boolean;
  twoFAToken: string;
  twoFAEmail: string;
}

class LoginContentContainer extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      isProcessingLogIn: false,
      isProcessingTermsOfService: false,
      termsOfServiceModalIsVisible: false,
      isTwoFALogin: false,
      twoFAToken: "",
      twoFAEmail: "",
    };
  }

  public render = () => (
    <LoginContentView
      appProps={this.props.appProps}
      isSpinningLoginCard={this.state.isProcessingLogIn}
      isSpinningTermsOfServiceModal={this.state.isProcessingTermsOfService}
      loginFormProps={this.generateLoginFormProps()}
      termsOfServiceModalProps={this.generateTermsOfServiceModalProps()}
      twoFALoginProps={this.generateTotpLoginProps()}
      isTwoFALogin={this.state.isTwoFALogin}
      handleCloseTwoFAClick={this.handleCloseTwoFAClick}
      handleEmailOtp={this.handleEmailOtp}
    />
  );

  private generateLoginFormProps = (): ILoginFormProps => ({
    onClickLogIn: async ({ email, password }): Promise<string> => {
      this.setState({ isProcessingLogIn: true });

      try {
        let loginUuid = uuidv4();
        let response = await VoiceGainAuthSvcApi.fetchPreLogin(
          loginUuid,
          password.length
        );
        if (response.ok) {
          let preLoginJson = await response.json();
          const passUuid = CryptoService.createXorBase64(
            password,
            preLoginJson.key
          );
          response = await VoiceGainAuthSvcApi.fetchLogin(
            email,
            passUuid,
            loginUuid
          );
          if (response.ok) {
            let json = await response.json();
            console.log(json);
            if (
              json.loginStatus &&
              json.loginStatus === "requires2FaTotp" &&
              json.twoFAToken
            ) {
              this.setState({
                twoFAToken: json.twoFAToken,
                twoFAEmail: email,
                isTwoFALogin: true,
              });
              return "2fa needed";
            } else if (!json.permission) {
              return "Username/password incorrect!";
            } else if (
              json.permission &&
              json.permission.indexOf("cmp") === -1
            ) {
              return "You do not have CMP authorization to access this application.";
            } else {
              const {
                account: {
                  accountId,
                  termsAcceptedBy: termsOfServiceAreAccepted,
                  webName: companyName,
                },
                api: apiKey,
                nonce: nonces,
                stok: sessionToken,
                userId,
              }: ILogInResponseBody = json;

              if (termsOfServiceAreAccepted) {
                await SessionService.clearSession();
                await SessionService.setSession({
                  accountId,
                  apiKey,
                  authToken: CryptoService.generateAuthToken(
                    email,
                    apiKey,
                    sessionToken
                  ),
                  companyName,
                  email,
                  nonces,
                  userId,
                });
                this.props.logIn();
              } else {
                this.setState({
                  stagedSession: {
                    accountId,
                    apiKey,
                    authToken: CryptoService.generateAuthToken(
                      email,
                      apiKey,
                      sessionToken
                    ),
                    companyName,
                    email,
                    nonces,
                    userId,
                  },
                  termsOfServiceModalIsVisible:
                    termsOfServiceAreAccepted === null,
                });
              }
              message.success("Logged in");
              return "success";
            }
          } else {
            return response ?response.status === 403 ? "Incorrect password or email" :  `${response.status}: Error signing in` : "Error signing in";
          }
        } else {
          return response ?response.status === 403 ? "Incorrect password or email" :  `${response.status}: Error signing in` : "Error signing in";
        }
      } catch (error: any) {
        message.error(error.message, 5);
        return error.messag;
      } finally {
        this.setState({ isProcessingLogIn: false });
      }
    },
  });

  private parseErrorMessage(input: any) {
    const errorMessage = input.error.reason;
    const adjustedErrorMessage =
      errorMessage === "incorrect totpCode"
        ? "Incorrect OTP code"
        : errorMessage === "missing sessionId"
        ? "Time to enter OTP code has expired. Please login again."
        : errorMessage;
    return adjustedErrorMessage;
  }

  private generateTotpLoginProps = (): ITwoFALoginFormProps => ({
    onClickLogIn: async ({ otp }) => {
      this.setState({ isProcessingLogIn: true });
      const email = this.state.twoFAEmail;

      try {
        let response = await VoiceGainAuthSvcApi.fetchTotpLogin(
          this.state.twoFAToken,
          otp.join("")
        );
        if (response.ok) {
          let json = await response.json();
          const {
            account: {
              accountId,
              termsAcceptedBy: termsOfServiceAreAccepted,
              webName: companyName,
            },
            api: apiKey,
            nonce: nonces,
            stok: sessionToken,
            userId,
          }: ILogInResponseBody = json;

          if (termsOfServiceAreAccepted) {
            await SessionService.clearSession();
            await SessionService.setSession({
              accountId,
              apiKey,
              authToken: CryptoService.generateAuthToken(
                email,
                apiKey,
                sessionToken
              ),
              companyName,
              email,
              nonces,
              userId,
            });
            this.props.logIn();
          } else {
            this.setState({
              stagedSession: {
                accountId,
                apiKey,
                authToken: CryptoService.generateAuthToken(
                  email,
                  apiKey,
                  sessionToken
                ),
                companyName,
                email,
                nonces,
                userId,
              },
              termsOfServiceModalIsVisible: termsOfServiceAreAccepted === null,
            });
          }
          message.success("Logged in");
        } else if (response.status === 401) {
          const errorResponseJson = await response.json();
          const adjustedErrorMessage =
            this.parseErrorMessage(errorResponseJson);
          message.error(adjustedErrorMessage);
        } else if (response.status === 404) {
          message.error("OTP has expired. Please login again!");
          this.setState({ isTwoFALogin: false });
        } else {
          const errorResponseJson = await response.json();
          const adjustedErrorMessage =
            this.parseErrorMessage(errorResponseJson);
          message.error(adjustedErrorMessage);
          this.setState({ isTwoFALogin: false });
        }
      } catch (error: any) {
        message.error(error.message, 5);
      } finally {
        this.setState({ isProcessingLogIn: false });
      }
    },
  });

  private handleCloseTwoFAClick = () => {
    this.setState({ isTwoFALogin: false });
  };
  private handleEmailOtp = async () => {
    try {
      VoiceGainAuthSvcApi.fetchEmailOtpLogin(this.state.twoFAToken).then(
        (response) => {
          if (response.ok) {
            message.success("Check you email for the one time time token");
          } else {
            message.error("Error in sending email");
            this.setState({ isTwoFALogin: false });
          }
        }
      );
    } catch {
      message.error("Error in sending email");
      this.setState({ isTwoFALogin: false });
    }
  };

  private generateTermsOfServiceModalProps = (): ITermsOfServiceModalProps => ({
    isVisible: this.state.termsOfServiceModalIsVisible,
    onAcceptTermsOfUse: () => {
      const { stagedSession } = this.state;
      this.setState({ isProcessingTermsOfService: true });
      if (stagedSession !== undefined) {
        const nonces = stagedSession.nonces.slice();
        const nonce = nonces.pop();
        if (nonce !== undefined) {
          // 1. Update server-side ToS flag
          VoiceGainAuthSvcApi.acceptTermsOfUse(
            stagedSession.accountId,
            stagedSession.apiKey,
            stagedSession.authToken,
            nonce,
            stagedSession.userId
          )
            .then((response) => {
              if (response.ok) {
                return;
              } else {
                throw new Error("Error accepting terms of use");
              }
            })
            .then(() =>
              // 2. Hide ToS modal
              this.setState({ termsOfServiceModalIsVisible: false }, () =>
                // 3. Save staged session to browser storage
                SessionService.clearSession().then(() =>
                  SessionService.setSession({ ...stagedSession, nonces }).then(
                    () =>
                      // 4. Set application status to "logged in" in Redux
                      this.props.logIn()
                  )
                )
              )
            )
            .catch((error) => {
              message.error(error.message);
            })
            .finally(() =>
              this.setState({ isProcessingTermsOfService: false })
            );
        }
      }
    },
  });
}

export default LoginContentContainer;
